import React, { useMemo, useRef } from 'react';
import ChartsIndex from '@/pages/intelligence/components/ChartsComponents/ChartsIndex';
import { ChartType } from '@/pages/intelligence/pages/TopCharts/_config/enums';
import useLineCharts from '@/pages/intelligence/components/Chart/hooks/useLineCharts';
import {
  showTimeByDateType,
  newMathFormat,
  getCountryNameByCountryCode,
} from '@/pages/intelligence/utils/commonUtils';
import { RenderType } from '@/pages/intelligence/pages/_config/topchartsInterface';
import useSinglePieCharts from '@/pages/intelligence/components/Chart/hooks/useSinglePieCharts';
import useBarCharts from '@/pages/intelligence/components/Chart/hooks/useBarCharts';
import { ECHARTSCOLOR } from '@/pages/intelligence/utils/echartsConst';
import moment from 'moment';
import { getIfPercentage } from '@/pages/intelligence/utils/common';
import type { IChartsIndexInstance } from '@intelligence/components/ChartsComponents/ChartsIndex';

// 每个榜单，组装好各类可视化需要的数据，传递给可视化统一组件
const Visualis: React.FC<any> = (props: any) => {
  const {
    chartType = ChartType.Line,
    dateType,
    startDate,
    endDate,
    lineData = [], // 折线 数据
    isNodata,
    imageType,
    metric,
    titleNow,
    chartLoading,
    setChartLoading,
    topChartsType,
    renderType,
    tableData,
    gameName,
    regionsCountries,
  } = props;

  // console.log('Visualis', props);
  const cardRef = useRef(null);
  const chartsInstance = useRef<IChartsIndexInstance>(null);
  const allTableData = useMemo(() => {
    return tableData?.find((item: any) => item.name == 'All Games') || {};
  }, [tableData]);

  // const allLineData = useMemo(() => {
  //   return lineData?.find((item: any) => item.name == 'All Games') || {};
  // }, [lineData]);

  const normalTableList = useMemo(() => {
    return tableData?.filter((item: any) => item.name != 'All Games') || [];
  }, [tableData]);

  const normalLineList = useMemo(() => {
    return lineData?.filter((item: any) => item.name != 'All Games') || [];
  }, [lineData]);

  const isPercentLine = useMemo(() => {
    switch (renderType) {
      case RenderType.GROWTH_ONLY:
        return true;
      default:
        return false;
    }
  }, [tableData, renderType]);

  const barNameList = useMemo(() => {
    const tempListTemp =
      normalTableList?.map((item: any) => {
        const nameStr = item?.name || item?.market || '';
        return nameStr;
      }) || [];
    return [...tempListTemp, 'Others'];
  }, [tableData]);

  const barSeriesData = useMemo(() => {
    const brList: any = [];
    let allNum = 0;
    let sum = 0;
    const labelObj = {
      show: true,
      position: 'top',
      color: '#444',
      distance: 4,
      fontSize: 12,
      formatter: (params: any) =>
        `${newMathFormat(Number(params.value), true) + (getIfPercentage(metric) ? '%' : '')}`,
    };
    const itemObj =
      Array.isArray(allTableData.games_metrics) && allTableData.games_metrics?.length > 0
        ? allTableData.games_metrics[0]
        : [];
    allNum = itemObj?.metric?.find((subItem: any) => subItem.metric == metric)?.value || 0;

    normalTableList?.forEach((item: any, i: any) => {
      const itemObj =
        Array.isArray(item.games_metrics) && item.games_metrics?.length > 0
          ? item.games_metrics[0]
          : [];
      const barNum = itemObj?.metric?.find((subItem: any) => subItem.metric == metric)?.value || 0;

      const objTemp = {
        type: 'bar',
        name: item?.name,
        value: barNum,
        unifiedId: item.id,
        cover: item.cover,
        itemStyle: {
          color: ECHARTSCOLOR[i],
        },
        label: labelObj,
      };
      sum += barNum;

      brList.push(objTemp);
    });

    const objOthers = {
      type: 'bar',
      name: 'Others',
      unifiedId: 'Others',
      value: allNum - sum,
      cover: '',
      itemStyle: {
        color: ECHARTSCOLOR[tableData?.length * 1 - 1],
      },
      label: labelObj,
    };
    return [...brList, objOthers];
  }, [tableData, metric]);

  // 获取柱状图hooks
  const { barOption } = useBarCharts({
    seriesData: barSeriesData,
    barOrPieData: barSeriesData,
    setChartLoading,
    // titleNow: titleNow,
    barNameList,
    // topChartsType: 'pcconsole-country-bar',
    cardWidth: cardRef?.current?.clientWidth,
    chartType,
    ifNeedSourceImg: false,
    secondTitleRight: titleNow,
    startDate,
    endDate,
  });

  const pieSeriesData = useMemo(() => {
    const piList: any = [];
    let allNum = 0;
    let sum = 0;

    tableData?.forEach((item: any, i: any) => {
      const itemObj =
        Array.isArray(item.games_metrics) && item.games_metrics?.length > 0
          ? item.games_metrics[0]
          : [];
      const metricNum =
        itemObj?.metric?.find((subItem: any) => subItem.metric == metric)?.value || 0;
      if (item.name == 'All Games') {
        allNum = metricNum;
      } else {
        const nameStr = item?.name;
        const objTemp = {
          name: nameStr,
          value: metricNum,
        };
        sum += metricNum;
        piList.push(objTemp);
      }
    });
    const objOthers = {
      name: 'Others',
      value: allNum - sum,
    };
    return [...piList, objOthers];
  }, [tableData, metric]);

  // 获取饼图hooks
  const { pieOption } = useSinglePieCharts({
    pieData: pieSeriesData,
    pieTitle: gameName,
    setChartLoading,
    chartLoading,
    chartType,
    chartsInstance,
    titleNow,
    startDate,
    endDate,
  });

  // 组装rpd折线图数据
  const getRpdTypeData = (list: any) => {
    if (!Array.isArray(list) || list?.length < 1) return [];

    const listTemp = list.sort((a: any, b: any) => moment(a.date) - moment(b.date));

    const arr: any = [];
    for (const i in listTemp) {
      const metricNum = listTemp[i].value ? listTemp[i]?.value : null;
      arr.push({
        time: showTimeByDateType(listTemp[i].date, dateType?.toLocaleLowerCase()),
        value: isPercentLine ? metricNum * 100 : metricNum,
      });
    }
    return arr;
  };

  const lineSeriesData = useMemo(() => {
    const arrGameData: any = [];
    for (const n in normalLineList) {
      const gameObj = normalLineList[n];
      const objTemp = {
        cover: gameObj.cover,
        id: gameObj.id,
        name: gameObj?.name,
        history: getRpdTypeData(gameObj.history || []),
      };
      arrGameData.push(objTemp);
    }
    // arrGameData = [
    //   ...arrGameData,
    //   {
    //     id: 'Others',
    //     name: 'Others',
    //     history: getOthersData(allLineData.history || [], normalLineList),
    //   }
    // ]
    const temp = {
      source: Array.isArray(arrGameData) ? arrGameData : [],
    };
    return temp;
  }, [lineData, metric]);

  // 获取折线图hooks
  const { lineOption } = useLineCharts({
    lineData: lineSeriesData,
    metric: 'source',
    // topChartsType: 'pcconsole-stats-line',
    imageType,
    secondTitleRight: titleNow,
    chartType,
    setChartLoading,
    dateType,
    isPercentLine,
    ifNeedMoveZero: false,
  });

  return (
    <>
      <div className="card-line" ref={cardRef}>
        <ChartsIndex
          ref={chartsInstance}
          chartLoading={chartLoading}
          lineOption={lineOption}
          barOption={barOption}
          pieOption={pieOption}
          chartType={chartType}
          isNodata={isNodata}
        />
      </div>
    </>
  );
};

export default Visualis;
